import { ApplicationData } from '../types';
import { GoogleGenAI, Type } from "@google/genai";

/**
 * CHUI API SERVICE
 * Connects to Google Apps Script Backend via VITE_BACKEND_URL environment variable.
 */

const getBackendUrl = () => {
  try {
    const url = (import.meta as any).env?.VITE_BACKEND_URL || '';
    // If it's a placeholder or too short, treat as no-backend
    if (url.length < 10 || url.includes('placeholder')) return '';
    return url.trim();
  } catch (e) {
    return '';
  }
};

const BACKEND_URL = getBackendUrl();

const handleResponse = async (res: Response) => {
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Directorate Uplink Error (${res.status}): ${errorText || 'The server returned an invalid response.'}`);
  }
  return res.json();
};

export const apiService = {
  status: {
    isLive: () => !!BACKEND_URL && BACKEND_URL.startsWith('http'),
  },

  applications: {
    submit: async (data: ApplicationData) => {
      // Robust check for live environment
      if (!BACKEND_URL || !BACKEND_URL.startsWith('http')) {
        console.warn("VITE_BACKEND_URL is missing or invalid. Triggering Secure Demo Intake.");
        await new Promise(resolve => setTimeout(resolve, 2000));
        return { 
          status: "success", 
          reference_id: `DEMO-${Math.random().toString(36).substr(2, 6).toUpperCase()}` 
        };
      }

      try {
        const res = await fetch(BACKEND_URL, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(data),
        });
        return await handleResponse(res);
      } catch (networkError: any) {
        console.error("Network Exception:", networkError);
        throw new Error(`The secure uplink could not be established. Network error or server unreachable.`);
      }
    },

    list: async () => {
      if (!BACKEND_URL || !BACKEND_URL.startsWith('http')) {
        // PRODUCTION: Mock data cleared. Registry starts empty.
        return [];
      }
      
      try {
        const res = await fetch(`${BACKEND_URL}?action=fetch`, {
          method: 'GET',
          mode: 'cors',
        });
        return handleResponse(res);
      } catch (e) {
        throw new Error("Failed to fetch application registry.");
      }
    }
  },

  ai: {
    chat: async (message: string, context?: any) => {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const promptContext = context ? ` [User Business Context: ${JSON.stringify(context)}]` : '';
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: message + promptContext,
        config: {
          systemInstruction: 'You are the Chui AI Growth Coach for Kenyan MSMEs. Provide high-level character-based credit advice and scaling strategies. Use local Kenyan business context and Swahili greetings where appropriate.',
        },
      });
      return { text: response.text || "Coach is currently offline." };
    },
    growthSimulate: async (data: any) => {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: `Analyze the following MSME data and simulate growth after 18 capital tranches. Data: ${JSON.stringify(data)}`,
        config: {
          systemInstruction: 'You are the Chui Growth Engine. Analyze business data and project growth impact. Return a structured JSON response.',
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              projectedUplift: { type: Type.STRING },
              revenueImpact: { type: Type.STRING },
              riskRating: { type: Type.STRING },
            },
            required: ['projectedUplift', 'revenueImpact', 'riskRating'],
          },
        },
      });
      try {
        const jsonStr = response.text || '{}';
        return JSON.parse(jsonStr.trim());
      } catch (e) {
        return { projectedUplift: "+20.0%", revenueImpact: "KES 20,000", riskRating: "Requires Data Update" };
      }
    }
  }
};